/**
 * Entering a table you are not seated at.
 *
 * ⚠ **`POST /api/campaigns/:id/join` had existed since Phase 3 and no client
 * ever called it.** The lobby already labelled an owned-but-unseated campaign
 * "yours to set up — no character seated", and its own name link, and the
 * campaign wizard's "Go to the table" button, both still pointed straight at
 * the live table — which opens a socket connection that refuses anyone with
 * no seat, verbatim: `error:event` answers "campaign not found" for the exact
 * reason `requireMembership` does on the REST side. A player who had just
 * created their own table got the same banner a stranger would.
 *
 * The button just asks which of the user's characters to use, and says
 * plainly when none are eligible. This is that screen, rendered by
 * `CampaignPage` in place of the table whenever the caller holds
 * no seat here — the single gatekeeper, so a bookmark, the lobby link and the
 * wizard's button all land on the same, working answer rather than three
 * copies of the same check.
 *
 * **Eligible excludes a character already seated at another table** — the
 * same rule the campaign wizard's own character step enforces, greyed with
 * the same reason, because `POST /join` refuses it with a 409 and offering it
 * enabled would be a button whose only outcome is a refusal.
 */

import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { api, ApiError } from '../api.js';
import { useAuth } from '../auth/store.js';
import { optionLabel } from '../characters/options.js';

/**
 * Whether one of the caller's OWN characters already holds a seat here.
 *
 * Pure, so the decision is testable without an effect or a network call —
 * the same split this project uses for `characterSteps` and
 * `combatPointsSpent`: the logic is a function, the component is a shell
 * around it.
 */
export function isSeatedAt(
  characters: readonly { readonly seat?: { readonly campaignId: string } }[],
  campaignId: string,
): boolean {
  return characters.some((c) => c.seat?.campaignId === campaignId);
}

/**
 * Which of the caller's characters may join — excludes one already seated
 * anywhere, because `POST /join` refuses that with a 409 and offering it
 * enabled would be a button whose only outcome is a refusal.
 */
export function eligibleToJoin<T extends { readonly seat?: unknown }>(
  characters: readonly T[],
): T[] {
  return characters.filter((c) => c.seat === undefined);
}

interface CharacterSummary {
  character: { id: string; name: string; race: string };
  classes: { class: string; level: number }[];
  seat?: { campaignId: string; campaignName: string };
}

interface ReadinessRow {
  createdBy: string | null;
  worldMapKey: string | null;
  worldState: { worldMapScaleMiles?: number; worldSeeded?: boolean };
}

/**
 * The door in front of `RequireSeat` — refuses to show a live table to the
 * Owner until the campaign has a generated world, a map image and its scale.
 *
 * ⚠ **The one case this redirects is OWNER and NOT ready — `seated` is no
 * longer part of the condition.** It originally read `seated && isOwner
 * && !ready`, on the reasoning that an *unseated* Owner was an edge case
 * `RequireSeat`'s own join picker handled well enough. That stopped being
 * true the moment the campaign wizard's character step was removed: making a
 * character and making a campaign are independent acts, nobody picks one to
 * create a table any more, so an Owner is now *unseated by construction*
 * immediately after every single creation — the universal case, not the
 * edge one. Left as `seated && …`, this door simply stopped firing for the
 * table it exists to protect: the Owner would see `RequireSeat`'s "which
 * character is playing here?" picker for a campaign with no map and no
 * world, and only find out it was not ready when `POST /join` answered 409.
 *
 * - **Not the Owner** — `RequireSeat` already shows the join picker/refusal
 *   for these, exactly as it always has. There is no live table to
 *   accidentally reveal, so readiness is not this door's business for
 *   anyone but the one person who can act on it.
 * - **Owner, but ready** — nothing to redirect for.
 *
 * A pre-existing seated Owner of an unready table (the case this door was
 * first built for) is still covered — `seated` is simply no longer a
 * *precondition*, since an Owner does not stop being the Owner by holding a
 * seat too.
 */
export function RequireReady({ children }: { children: React.ReactNode }) {
  const { campaignId } = useParams();
  const userId = useAuth((s) => s.user?.id);
  const [redirect, setRedirect] = useState<'checking' | 'no' | 'yes'>('checking');

  useEffect(() => {
    if (!campaignId) return;
    let current = true;
    setRedirect('checking');
    void api
      .get<{ campaign: ReadinessRow }>(`/api/campaigns/${campaignId}`)
      .then((body) => {
        if (!current) return;
        const c = body.campaign;
        const ready =
          c.worldMapKey !== null &&
          c.worldState.worldMapScaleMiles !== undefined &&
          c.worldState.worldSeeded === true;
        const isOwner = c.createdBy !== null && c.createdBy === userId;
        setRedirect(isOwner && !ready ? 'yes' : 'no');
      })
      // A stranger gets 404 here (`requireCampaignScope`'s own refusal) — not
      // this door's question to answer either; fall through to `RequireSeat`.
      .catch(() => {
        if (current) setRedirect('no');
      });
    return () => {
      current = false;
    };
  }, [campaignId, userId]);

  if (!campaignId) return <>{children}</>;

  if (redirect === 'checking') {
    return (
      <p className="text-sm" role="status">
        Loading…
      </p>
    );
  }

  if (redirect === 'yes') {
    return <Navigate to={`/campaigns/${campaignId}/setup`} replace />;
  }

  return <>{children}</>;
}

/**
 * The route wrapper for `/campaigns/:campaignId` — the `RequireAuth` pattern
 * applied to "is there a seat here for you", which nothing in this codebase
 * had asked before reaching the socket. Renders the table when there is one,
 * this screen when there is not, and nothing else: `CampaignPage` itself is
 * unchanged and untouched by any of this, which is the whole point — the
 * table's own tests keep asserting exactly what they always did.
 */
export function RequireSeat({ children }: { children: React.ReactNode }) {
  const { campaignId } = useParams();
  const [seated, setSeated] = useState<'undetermined' | 'yes' | 'no'>('undetermined');
  // ⚠ **An explicit counter, not `seated === 'undetermined'`.** Deriving the
  // dependency from the state this effect itself sets flips it a second time
  // the moment the fetch resolves — `undetermined → 'no'` toggles the boolean
  // and reruns the effect once more for nothing. A counter only advances when
  // `onJoined` asks for a genuine recheck.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!campaignId) return;
    let current = true;
    setSeated('undetermined');
    void api
      .get<{ characters: { seat?: { campaignId: string } }[] }>('/api/characters')
      .then((body) => {
        if (!current) return;
        setSeated(isSeatedAt(body.characters, campaignId) ? 'yes' : 'no');
      })
      .catch(() => {
        if (current) setSeated('no');
      });
    return () => {
      current = false;
    };
  }, [campaignId, attempt]);

  if (!campaignId) return <>{children}</>;

  if (seated === 'undetermined') {
    return (
      <p className="text-sm" role="status">
        Loading…
      </p>
    );
  }

  if (seated === 'no') {
    return <EnterCampaign campaignId={campaignId} onJoined={() => setAttempt((n) => n + 1)} />;
  }

  return <>{children}</>;
}

/**
 * The character picker itself, exported so the campaign wizard's own last
 * step can embed it directly. `RequireSeat` above is the door that shows
 * this when somebody follows a bookmark or the wizard's own "Go to the
 * table" button straight in; the wizard's last step shows the identical
 * picker earlier, on the same data, so pressing that button is never the
 * first time an unseated Owner learns they need a character.
 */
export function EnterCampaign({
  campaignId,
  onJoined,
  heading = true,
}: {
  readonly campaignId: string;
  /** Called once the join succeeds, so the caller can re-check and swap in whatever comes next. */
  readonly onJoined: () => void;
  /**
   * Whether to render this component's own "Enter this table" heading.
   * Default `true` for `RequireSeat`'s use, where this is the whole page and
   * needs one. The wizard's last step embeds this same picker under its own
   * "6 · Open the table" heading — a second `<h1>` there would be a stray
   * heading on a page that already has one, not a courtesy.
   */
  readonly heading?: boolean;
}) {
  const [characters, setCharacters] = useState<CharacterSummary[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<{ characters: CharacterSummary[] }>('/api/characters')
      .then((body) => setCharacters(body.characters))
      .catch(() => setCharacters([]));
  }, []);

  if (characters === null) {
    return (
      <p className="text-sm" role="status">
        Loading…
      </p>
    );
  }

  const eligible = eligibleToJoin(characters);

  const join = (characterId: string) => {
    setBusyId(characterId);
    setError(null);
    void api
      .post(`/api/campaigns/${campaignId}/join`, { characterId })
      .then(onJoined)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'that seat was refused'))
      .finally(() => setBusyId(null));
  };

  return (
    <div className="space-y-4">
      {heading && <h1 className="text-lg font-semibold">Enter this table</h1>}

      {error && (
        <p role="alert" className="text-sm text-amber-800 dark:text-amber-300">
          {error}
        </p>
      )}

      {eligible.length === 0 ? (
        // No fallback to a read-only view: this ruleset gives the Owner
        // administration and gives play to a character, and there is a
        // separate page — the map setup page — for the first of those.
        <div className="space-y-2">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            You need a character to enter this campaign.
          </p>
          <Link to="/characters/new" className="text-sm underline underline-offset-2">
            New character
          </Link>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Which of your characters is playing here?
          </p>
          <ul className="flex flex-wrap gap-2 text-sm">
            {eligible.map(({ character, classes }) => (
              <li key={character.id}>
                <button
                  type="button"
                  disabled={busyId !== null}
                  onClick={() => join(character.id)}
                  className="rounded-md border border-neutral-300 px-3 py-1 disabled:opacity-40 dark:border-neutral-700"
                >
                  {busyId === character.id ? 'Joining…' : character.name}{' '}
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    {optionLabel(character.race)}{' '}
                    {classes.map((c) => `${optionLabel(c.class)} ${c.level}`).join(' / ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Seated elsewhere, shown rather than hidden — the same courtesy the
          campaign wizard's own character step offers, so a player can see
          where a character went rather than wondering why it is missing. */}
      {characters.some((c) => c.seat !== undefined) && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {characters
            .filter((c) => c.seat !== undefined)
            .map((c) => `${c.character.name} is already at ${c.seat!.campaignName}`)
            .join('; ')}
          .
        </p>
      )}
    </div>
  );
}
